// Global Instructions Rule Applied!

import {
  SET_JOURNAL_ENTRIES,
  APPEND_JOURNAL_ENTRIES,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  UPDATE_JOURNAL_ENTRY_RECORD,
  REMOVE_JOURNAL_ENTRY
} from './constants.ts'
import { JournalEntryRecord, JournalEntryRecordFields } from '../../data_models/journal_entry_record.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface JournalEntriesPaginatedResponse {
  data: JournalEntryRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface JournalEntriesState {
  data: JournalEntryRecord[]
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type JournalEntriesAction =
  | { type: typeof SET_JOURNAL_ENTRIES; payload: { response: JournalEntriesPaginatedResponse } }
  | { type: typeof APPEND_JOURNAL_ENTRIES; payload: { response: JournalEntriesPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof CLEAR_ACTIVE_FILTERS }
  | { type: typeof UPDATE_JOURNAL_ENTRY_RECORD; payload: { journalEntryId: string; updates: Record<string, any> } }
  | { type: typeof REMOVE_JOURNAL_ENTRY; payload: { journalEntryId: string } }

const journalEntriesReducer = (
  state: JournalEntriesState = {
    data: [],
    activeFilters: [],
    activeSorting: [],
    hasMore: false,
    currentPage: 1
  },
  action: JournalEntriesAction
): JournalEntriesState => {
  const idKey = JournalEntryRecordFields.journal_entries__id
  switch (action.type) {
    case SET_JOURNAL_ENTRIES: {
      const { response } = action.payload
      return { ...state, data: response.data, hasMore: response.hasMore, currentPage: 1 }
    }

    case APPEND_JOURNAL_ENTRIES: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }

    case SET_ACTIVE_FILTERS: {
      return { ...state, activeFilters: action.payload.filters }
    }

    case SET_ACTIVE_SORTING: {
      return { ...state, activeSorting: action.payload.sorting }
    }

    case CLEAR_ACTIVE_FILTERS: {
      return { ...state, activeFilters: [] }
    }

    case UPDATE_JOURNAL_ENTRY_RECORD: {
      const { journalEntryId, updates } = action.payload
      if (!state.data.some((r) => (r as any)[idKey] === journalEntryId)) return state
      return {
        ...state,
        data: state.data.map((r) => ((r as any)[idKey] === journalEntryId ? { ...r, ...updates } : r))
      }
    }

    case REMOVE_JOURNAL_ENTRY: {
      const { journalEntryId } = action.payload
      return { ...state, data: state.data.filter((r) => (r as any)[idKey] !== journalEntryId) }
    }

    default:
      return state
  }
}

export default journalEntriesReducer

export const setJournalEntries = (payload: { response: JournalEntriesPaginatedResponse }) => ({
  type: SET_JOURNAL_ENTRIES,
  payload
})

export const appendJournalEntries = (payload: { response: JournalEntriesPaginatedResponse }) => ({
  type: APPEND_JOURNAL_ENTRIES,
  payload
})

export const setJournalEntriesActiveFilters = (payload: { filters: FilterValue[] }) => ({
  type: SET_ACTIVE_FILTERS,
  payload
})

export const setJournalEntriesActiveSorting = (payload: { sorting: SortConfig[] }) => ({
  type: SET_ACTIVE_SORTING,
  payload
})

export const clearJournalEntriesActiveFilters = () => ({ type: CLEAR_ACTIVE_FILTERS })

export const updateJournalEntryRecord = (payload: { journalEntryId: string; updates: Record<string, any> }) => ({
  type: UPDATE_JOURNAL_ENTRY_RECORD,
  payload
})

export const removeJournalEntry = (payload: { journalEntryId: string }) => ({
  type: REMOVE_JOURNAL_ENTRY,
  payload
})

export const selectJournalEntryById = (state: any, journalEntryId: string) =>
  (state.journalEntries?.data ?? []).find(
    (r: any) => r[JournalEntryRecordFields.journal_entries__id] === journalEntryId
  )
